import { useCallback, useEffect, useMemo, useState } from 'react';

import { fetchEmployeeDetailsByEmployeeId } from '~/entities/Employee/api/employee.api';
// eslint-disable-next-line import/no-cycle
import { mapEmployeeDetails as mapEmployeeDetailsToEmployee } from '~/entities/Employee/utils/mapEmployeeDetails';

import { EmployeeInterface } from '~/entities/Employee/dto/employee-details.dto';
import { EmployeeContextType } from '~/entities/Employee/types/employee-context.types';
import { useUser } from '../auth/useUser';

interface UseComplianceEmployeeProps {
  initialEmployee?: EmployeeInterface | null;
  loadEmployeeFromSession?: boolean;
  onEmployeeChange?: (employee: EmployeeInterface | null) => void;
}

const fetchEmployeeFromAPI = async (
  employeeId: string
): Promise<EmployeeInterface | null> => {
  if (!employeeId) {
    console.warn('User email is not available in session');
    return null;
  }

  try {
    const employeeDetails = await fetchEmployeeDetailsByEmployeeId(employeeId);
    return mapEmployeeDetailsToEmployee(employeeDetails);
  } catch (error) {
    console.error('Failed to fetch employee details:', error);
    return null;
  }
};

export const useComplianceEmployee = ({
  initialEmployee = null,
  loadEmployeeFromSession = true,
  onEmployeeChange,
}: UseComplianceEmployeeProps = {}): EmployeeContextType => {
  const user = useUser();
  const userId = user.employeeId || '';

  const [employee, setEmployee] = useState<EmployeeInterface | null>(
    initialEmployee
  );
  const [isOnBehalf, setIsOnBehalf] = useState(false);
  const [onBehalfEmployee, setOnBehalfEmployee] =
    useState<EmployeeInterface | null>(null);
  const [isLoading, setIsLoading] = useState(
    loadEmployeeFromSession && !initialEmployee
  );

  useEffect(() => {
    if (!loadEmployeeFromSession || initialEmployee || employee || !userId) {
      return;
    }

    const fetchEmployeeFromSession = async () => {
      setIsLoading(true);

      try {
        const employeeData = await fetchEmployeeFromAPI(userId);

        setEmployee(employeeData);
        onEmployeeChange?.(employeeData);
      } catch (error) {
        console.error('Error loading employee:', error);

        const fallbackEmployee: EmployeeInterface = {
          id: 'UNKNOWN',
          name: 'User',
          email: 'user@ab-inbev.com',
        };

        setEmployee(fallbackEmployee);
        onEmployeeChange?.(fallbackEmployee);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEmployeeFromSession();
  }, [
    loadEmployeeFromSession,
    initialEmployee,
    employee,
    onEmployeeChange,
    userId,
  ]);

  const permissions = useMemo(
    () => employee?.permissions || [],
    [employee?.permissions]
  );

  const canSubmitOnBehalf = useMemo(() => true, []); // Note: for now, everyone can submit on behalf of others

  useEffect(() => {
    if (!canSubmitOnBehalf && isOnBehalf) {
      setIsOnBehalf(false);
      setOnBehalfEmployee(null);
    }
  }, [canSubmitOnBehalf, isOnBehalf]);

  useEffect(() => {
    if (!isOnBehalf) {
      setOnBehalfEmployee(null);
    }
  }, [isOnBehalf]);

  const setEmployeeWithCallback = useCallback(
    (newEmployee: EmployeeInterface | null) => {
      setEmployee(newEmployee);
      onEmployeeChange?.(newEmployee);
    },
    [onEmployeeChange]
  );

  const contextValue = useMemo(
    () => ({
      employee,
      setEmployee: setEmployeeWithCallback,
      isOnBehalf,
      setIsOnBehalf,
      onBehalfEmployee,
      setOnBehalfEmployee,
      permissions,
      canSubmitOnBehalf,
      isLoading,
    }),
    [
      employee,
      setEmployeeWithCallback,
      isOnBehalf,
      onBehalfEmployee,
      permissions,
      canSubmitOnBehalf,
      isLoading,
    ]
  );

  return contextValue;
};
